import { useQuery } from "@tanstack/react-query"
import axios from "axios"

import { TicketTypesResponse } from "types"

interface UseTicketTypesOptions {
  enabled?: boolean
}

/**
 * Custom hook for fetching ticket types within a registration bundle.
 *
 * Ticket types represent the actual purchasable items in a registration system.
 * Examples include "Early Bird General", "Standard VIP", "Student Discount", etc.
 * Each ticket type can belong to a ticket group for organizational purposes.
 *
 * Features:
 * - React Query integration with caching and background updates
 * - Support for admin vs. public ticket filtering
 * - Optional ticket group filtering to narrow results
 * - Automatic error handling with console logging
 * - Dynamic query key generation for optimal caching
 * - 5-minute optimistic caching to reduce API calls
 *
 * @param bundleUUID - The unique identifier for the registration bundle
 * @param ticketGroup - Optional ticket group ID to filter results by specific group
 * @param isAdmin - Whether to fetch admin-only tickets (defaults to public tickets)
 * @param options - Additional query configuration options
 * @returns React Query result object containing:
 *   - data: TicketsResponse with available ticket types
 *   - isLoading: Boolean indicating if the initial request is in progress
 *   - error: Error object if the request failed
 *   - refetch: Function to manually trigger a refetch
 *
 * @example
 * ```tsx
 * // Fetch all public tickets
 * const { data: tickets, isLoading } = useTicketTypesData(bundleUUID)
 *
 * // Fetch tickets for a specific group
 * const { data: vipTickets } = useTicketTypesData(bundleUUID, vipGroupId)
 *
 * // Fetch admin tickets
 * const { data: adminTickets } = useTicketTypesData(bundleUUID, null, true)
 *
 * return (
 *   <div>
 *     {tickets?.results?.map(ticket => (
 *       <TicketCard key={ticket.id} ticket={ticket} />
 *     ))}
 *   </div>
 * )
 * ```
 */
export const useTicketTypesData = (
  bundleUUID: string | undefined,
  ticketGroup?: number | null,
  isAdmin?: boolean,
  options?: UseTicketTypesOptions
) => {
  return useQuery<TicketTypesResponse>({
    queryKey: ["tickets", bundleUUID, isAdmin, ticketGroup],
    queryFn: async () => {
      try {
        const adminPath = isAdmin ? "admin-tickets" : ""
        const ticketGroupParam = ticketGroup
          ? `&ticket_group=${ticketGroup}`
          : ""

        const response = await axios.get<TicketTypesResponse>(
          `/api/registration/ticket-type-published/${adminPath}?bundle__uuid=${bundleUUID}${ticketGroupParam}`
        )
        return response.data
      } catch (error) {
        console.error("Error fetching ticket types:", error)
        throw error
      }
    },
    enabled: !!bundleUUID && options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}
